import { memo } from "react";
import { AbsoluteFill, Html5Audio, Img, Sequence } from "remotion";
import { KenBurns } from "../effects/ken-burns";
import { ZoomPulse } from "../effects/zoom-pulse";
import type { BackgroundVideoProps } from "./constants";
import { useAudioTracks } from "../hooks/use-audio-tracks";
import { VideoBackground } from "./video-background";

const Background = memo<{
	backgroundUrl: string;
	withEffect: boolean;
	effect?: string;
}>(({ backgroundUrl, withEffect, effect }) => {
	const isVideo = backgroundUrl
		.toLowerCase()
		.match(/\.(mp4|webm|mov|mkv)(\?|$)/);

	if (isVideo) {
		return <VideoBackground videoUrl={backgroundUrl} />;
	}

	if (!withEffect) {
		return (
			<Img
				src={backgroundUrl}
				style={{
					width: "100%",
					height: "100%",
					objectFit: "cover",
				}}
			/>
		);
	}

	if (effect === "zoompulse") {
		return <ZoomPulse imageUrl={backgroundUrl} />;
	}

	// Default to KenBurns if effect is not specified or unknown
	return <KenBurns imageUrl={backgroundUrl} />;
});

Background.displayName = "Background";

type CompositionProps = BackgroundVideoProps;

export const BackgroundVideo: React.FC<CompositionProps> = ({
	audioTracks,
	backgroundUrl,
	withEffect,
	effect,
}) => {
	const { isValid, sequences } = useAudioTracks({
		audioTracks,
	});

	if (!isValid) {
		return (
			<AbsoluteFill
				style={{
					backgroundColor: "#000",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div style={{ color: "white", fontFamily: "sans-serif" }}>
					No audio tracks provided.
				</div>
			</AbsoluteFill>
		);
	}

	return (
		<AbsoluteFill style={{ backgroundColor: "#000" }}>
			<AbsoluteFill>
				<Background
					backgroundUrl={backgroundUrl}
					withEffect={withEffect}
					effect={effect}
				/>
			</AbsoluteFill>

			{sequences.map((seq) => (
				<Sequence
					key={`audio-${seq.index}`}
					from={seq.startFrame}
					durationInFrames={seq.durationInFrames}
				>
					<Html5Audio src={seq.track.url} />
				</Sequence>
			))}
		</AbsoluteFill>
	);
};
